import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { jsx as _jsx } from "react/jsx-runtime";
export const CalendarContext = /*#__PURE__*/createContext(undefined);
export default function CalendarContextProvider({
  children,
  timezone
}) {
  const [hasScrolledToTime, setHasScrolledToTime] = useState(false);
  const callbacks = useRef([]);
  const subscribeToUpdates = useCallback(callback => {
    callbacks.current.push(callback);
    return () => {
      callbacks.current = callbacks.current.filter(item => item !== callback);
    };
  }, []);
  const [openPopover, setOpenPopover] = useState(null);
  const closeCurrentPopover = useCallback(() => setOpenPopover(null), []);
  const setPopoverState = useCallback((requestKey, newState = true) => setOpenPopover(newState === false ? null : requestKey), []);
  const hasPopoverOpen = !!openPopover;
  const previousOpenPopover = usePrevious(openPopover);
  useEffect(() => {
    if (openPopover !== previousOpenPopover) {
      callbacks.current.forEach(callback => {
        if (typeof callback === 'function') {
          callback(openPopover);
        }
      });
    }
  }, [openPopover, previousOpenPopover]);
  const value = useMemo(() => ({
    closeCurrentPopover,
    hasScrolledToTime,
    hasPopoverOpen,
    setHasScrolledToTime,
    setPopoverState,
    subscribeToUpdates,
    timezone
  }), [closeCurrentPopover, hasScrolledToTime, hasPopoverOpen, setPopoverState, subscribeToUpdates, timezone]);
  return /*#__PURE__*/_jsx(CalendarContext.Provider, {
    value: value,
    children: children
  });
}
export function useCalendarContext() {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error('useCalendarContext must be used within a CalendarContextProvider');
  }
  return context;
}